import React, { useState, useEffect } from "react";
import {
  Grid,
  Text,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { EventFormValues } from "./types";
import { findClosestOrganizerMatch } from "utils/organisers";
import { useCreateOrganizer } from "hooks/organizers";
import { SelectClearable } from "ui/SelectClearable";

interface EventFormOrganiserProps {
  form: UseFormReturnType<EventFormValues>;
  organizers: { id: number; name: string }[];
  currOrganizerId?: number;
  applicationOrganizerInfo?: {
    organiserName?: string;
    organiserPhone?: string;
    organiserEmail?: string;
    organiserMessage?: string;
    organiserLogo?: string;
  };
  t: (key: string) => string;
  language: string;
  onOrganizerSelect?: (organizer: { id: number; name: string } | null) => void;
  onAddOrganizerClick?: (info: any) => void;
  pendingOrganizerId?: number | null;
  onPendingOrganizerHandled?: () => void;
  /**
   * Desk mode: never run the AI organizer matcher, which calls the paid PROD
   * `openai` function (Pitfall 5). The desk prefills `user` from the row.
   */
  disableAiMatch?: boolean;
}

export const EventFormOrganiser = ({
  form,
  organizers,
  currOrganizerId,
  applicationOrganizerInfo,
  t,
  language,
  onOrganizerSelect,
  onAddOrganizerClick,
  pendingOrganizerId,
  onPendingOrganizerHandled,
  disableAiMatch = false,
}: EventFormOrganiserProps) => {
  const [creatingOrganizer, setCreatingOrganizer] = useState<string | null>(
    null
  );
  const [findingOrganizer, setFindingOrganizer] = useState(false);
  const [foundOrganizer, setFoundOrganizer] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Use a ref to track if we've already searched for an organizer
  const searchedOrganizerRef = React.useRef<string | null>(null);
  // Use a ref to track the last AI suggestion
  const lastAISuggestionRef = React.useRef<string | null>(null);
  // Ref to track last AI match to prevent repeated logs and updates
  const lastAIMatchRef = React.useRef<string | null>(null);

  const createOrganizer = useCreateOrganizer(() => {
    // Refresh organizers list
    // Note: This should happen automatically due to query invalidation in the hook
  });

  // Use AI to find organizer match when applicationOrganizerInfo.organiserName is provided
  useEffect(() => {
    // Before any call: the desk must never reach the prod matcher.
    if (disableAiMatch) {
      return;
    }
    const organiserName = applicationOrganizerInfo?.organiserName;

    // Skip if there's no name, no organizers, or we've already searched for this name
    if (
      !organiserName ||
      organizers.length === 0 ||
      searchedOrganizerRef.current === organiserName
    ) {
      return;
    }

    // Only reset userHasEdited if the AI suggestion is new
    if (lastAISuggestionRef.current !== organiserName) {
      setUserHasEdited(false);
      lastAISuggestionRef.current = organiserName;
    }

    // Remember that we've searched for this name
    searchedOrganizerRef.current = organiserName;
    setFindingOrganizer(true);

    // First try exact match (case-insensitive)
    const exactMatch = organizers.find(
      (org) => org.name.toLowerCase() === organiserName?.toLowerCase()
    );
    if (exactMatch) {
      setFoundOrganizer(exactMatch);
      setFindingOrganizer(false);
    } else {
      // If no exact match, use AI to find closest match
      findClosestOrganizerMatch(organiserName, organizers)
        .then((match) => {
          setFoundOrganizer(match);
        })
        .catch((error) => {
          console.error("Error finding organizer match:", error);
        })
        .finally(() => {
          setFindingOrganizer(false);
        });
    }
  }, [applicationOrganizerInfo?.organiserName, organizers, disableAiMatch]);

  // Set organizer search value when found organizer exists
  useEffect(() => {
    if (foundOrganizer && !userHasEdited) {
      // Only update if the value is actually different
      if (form.values.user !== foundOrganizer.id) {
        setSelectedValue(foundOrganizer.id.toString());
        setSearchValue(foundOrganizer.name);
        form.setFieldValue("user", foundOrganizer.id);
        setUserHasEdited(false); // Reset so AI can update again

        // Notify parent component of selection if callback provided
        if (onOrganizerSelect) {
          onOrganizerSelect(foundOrganizer);
        }

        // Log successful AI match if it wasn't an exact match and not already logged
        if (
          applicationOrganizerInfo?.organiserName &&
          applicationOrganizerInfo.organiserName?.toLowerCase() !==
            foundOrganizer.name.toLowerCase() &&
          lastAIMatchRef.current !== foundOrganizer.name
        ) {
          lastAIMatchRef.current = foundOrganizer.name;
        }
      }
    }
  }, [
    foundOrganizer,
    form,
    applicationOrganizerInfo,
    onOrganizerSelect,
    userHasEdited,
  ]);

  // Reset lastAIMatchRef and userHasEdited when a new application is loaded
  useEffect(() => {
    lastAIMatchRef.current = null;
    setUserHasEdited(false);
  }, [applicationOrganizerInfo?.organiserName]);

  // Initialize selectedValue and searchValue from form's current value
  useEffect(() => {
    const currentUserId = form.values.user;
    if (currentUserId && organizers.length > 0) {
      const currentOrganizer = organizers.find(
        (org) => org.id === currentUserId
      );
      if (
        currentOrganizer &&
        selectedValue !== currentOrganizer.id.toString()
      ) {
        setSelectedValue(currentOrganizer.id.toString());
        setSearchValue(currentOrganizer.name);
      }
    }
  }, [form.values.user, organizers, selectedValue]);

  // Effect: if pendingOrganizerId is set and exists in organizers, select it and clear pending
  useEffect(() => {
    if (pendingOrganizerId && organizers.length > 0) {
      const found = organizers.find((org) => org.id === pendingOrganizerId);
      if (found) {
        setSelectedValue(found.id.toString());
        setSearchValue(found.name);
        form.setFieldValue("user", found.id);
        if (onOrganizerSelect) {
          onOrganizerSelect(found);
        }
        if (onPendingOrganizerHandled) {
          onPendingOrganizerHandled();
        }
      }
    }
  }, [pendingOrganizerId, organizers, form, onOrganizerSelect, onPendingOrganizerHandled]);

  const handleCreateOrganizer = async (orgName: string) => {
    if (!orgName.trim()) return;

    try {
      setCreatingOrganizer(orgName);
      await createOrganizer.mutateAsync({
        name: orgName,
        email: "",
        phone: "",
        whatsapp: "",
        is_municipality: false,
        preferred_language: language,
        website: "",
        description: "",
        description_pt: "",
        enable_weekly: false,
      });
      // The effect for pendingOrganizerId will handle selection
    } catch (error) {
      console.error("Failed to create organizer:", error);
    } finally {
      setCreatingOrganizer(null);
    }
  };

  const handleOrganizerSelect = async (value: string | null) => {
    setUserHasEdited(true);
    if (!value) {
      setSelectedValue(null);
      setSearchValue("");
      form.setFieldValue("user", null);
      if (onOrganizerSelect) {
        onOrganizerSelect(null);
      }
      return;
    }

    if (value.startsWith("__create__:")) {
      const newOrganizerName = value.replace("__create__:", "");
      if (newOrganizerName.trim()) {
        await handleCreateOrganizer(newOrganizerName);
      }
    } else if (value === "__add_new__") {
      // Create new organizer with the current search value
      if (searchValue.trim()) {
        await handleCreateOrganizer(searchValue);
      }
    } else {
      const selectedOrganizer = organizers.find(
        (org) => org.id.toString() === value
      );
      if (selectedOrganizer) {
        setSelectedValue(selectedOrganizer.id.toString());
        setSearchValue(selectedOrganizer.name);
        form.setFieldValue("user", selectedOrganizer.id);
        if (onOrganizerSelect) {
          onOrganizerSelect(selectedOrganizer);
        }
      }
    }
  };

  // Don't render if currOrganizerId exists
  if (currOrganizerId) {
    return null;
  }

  // Generate options for the select, including the "create new" option
  const organizerOptions = organizers.map((org) => ({
    value: org.id.toString(),
    label: org.name,
  }));

  // Determine whether to show "create new organizer" option
  const trimmedSearchValue = searchValue.trim();
  const hasExactMatch =
    trimmedSearchValue !== "" &&
    organizers.some(
      (org) => org.name.toLowerCase() === trimmedSearchValue.toLowerCase()
    );

  // Check if we're in application edit mode (when applicationOrganizerInfo exists)
  const isApplicationEditMode = !!applicationOrganizerInfo?.organiserName;

  // Add the option to create a new organizer if the search doesn't match any existing organizers
  // We'll use the searchValue as the organizer name internally, but display just "Add organizer" in the UI
  const createNewLabel = t("events.add_organizer");
  const createNewValue = `__create__:${trimmedSearchValue}`;

  // Only add "create new" option if we're not in application edit mode
  if (trimmedSearchValue !== "" && !hasExactMatch && !isApplicationEditMode) {
    // Remove existing create option if any
    const createIndex = organizerOptions.findIndex((opt) =>
      opt.value.startsWith("__create__:")
    );
    if (createIndex >= 0) {
      organizerOptions.splice(createIndex, 1);
    }

    // Add the new create option
    organizerOptions.push({ value: createNewValue, label: createNewLabel });
  }

  // Only add the generic "Add Organizer" option if there's no search value and not in application edit mode
  if (
    !trimmedSearchValue &&
    !organizerOptions.some((option) => option.value === "__add_new__") &&
    !isApplicationEditMode
  ) {
    organizerOptions.push({
      value: "__add_new__",
      label: t("events.add_organizer"),
    });
  }

  // Determine if the orange border should be shown
  const aiSuggestedName = applicationOrganizerInfo?.organiserName;
  const aiSuggestionChosen =
    aiSuggestedName &&
    selectedValue === foundOrganizer?.name &&
    aiSuggestedName?.toLowerCase() !== foundOrganizer?.name.toLowerCase();

  // Determine if the original name should be shown in parenthesis
  const selectedOrganizer = organizers.find(
    (org) => org.id.toString() === selectedValue
  );
  const showOriginalName =
    applicationOrganizerInfo?.organiserName &&
    (!selectedOrganizer ||
      applicationOrganizerInfo.organiserName?.toLowerCase() !==
        selectedOrganizer.name.toLowerCase());

  return (
    <Grid.Col span={6}>
      <Text size="sm" fw={500} mb={4}>
        {t("events.organizer")}
      </Text>

      <SelectClearable
        value={selectedValue}
        data={organizerOptions}
        placeholder={t("events.select_or_create_organizer")}
        onChange={(val) => {
          setSelectedValue(val);
          const found = organizers.find((org) => org.id.toString() === val);
          setSearchValue(found ? found.name : searchValue);
          handleOrganizerSelect(val);
        }}
        loading={findingOrganizer || !!creatingOrganizer}
        style={
          aiSuggestionChosen
            ? { border: "2px solid orange", borderRadius: 4 }
            : undefined
        }
        emptyMessage={
          searchValue.trim() && !isApplicationEditMode ? (
            <Text color="blue" style={{ cursor: "pointer" }}>
              {t("events.add_organizer")}
            </Text>
          ) : (
            t("events.no_organizers_found")
          )
        }
        onEmptyClick={() => {
          if (searchValue.trim() && !isApplicationEditMode) {
            handleCreateOrganizer(searchValue.trim());
          }
        }}
        onInputChange={(value: string) => {
          setUserHasEdited(true);
          setSearchValue(value);
        }}
      />

      {showOriginalName && (
        <Text size="xs" color="dimmed" mt={2}>
          ({applicationOrganizerInfo.organiserName}){" "}
          <Text
            span
            size="xs"
            color="blue"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() =>
              onAddOrganizerClick &&
              onAddOrganizerClick(applicationOrganizerInfo)
            }
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if ((e.key === "Enter" || e.key === " ") && onAddOrganizerClick) {
                onAddOrganizerClick(applicationOrganizerInfo);
              }
            }}
          >
            {t("common.add") || "add"}
          </Text>
        </Text>
      )}
      {creatingOrganizer && (
        <Text size="xs" mt={5}>
          {`${t("events.creating_organizer")}: ${creatingOrganizer}...`}
        </Text>
      )}
    </Grid.Col>
  );
};
